// frontend/src/pages/BomManagement.jsx
import React, { useState, useEffect, useRef } from "react";
import Page from "../ui/Page";
import { Button } from "../ui/UIComponents";

/**
 * Clean Local Auto-Expanding Textarea Component
 * Automatically shifts height dynamically as the user inputs data
 */
function AutoExpandingTextArea({ label, value, onChange, placeholder, style }) {
  const textareaRef = useRef(null);

  useEffect(() => {
    const tx = textareaRef.current;
    if (!tx) return;
    tx.style.height = "auto";
    tx.style.height = `${tx.scrollHeight}px`;
  }, [value]);

  return (
    <label style={{ display: "block", width: "100%" }}>
      {label && <span className="label">{label}</span>}
      <textarea
        ref={textareaRef}
        className="text-input"
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        style={{
          minHeight: "42px",
          resize: "none",
          overflowY: "hidden",
          lineHeight: "1.5",
          boxSizing: "border-box",
          fontFamily: "inherit",
          width: "100%",
          ...style
        }}
      />
    </label>
  );
}

export default function BomManagement() {
  // --- State Architecture ---
  const [submitting, setSubmitting] = useState(false);
  const [loadingRecord, setLoadingRecord] = useState(false);
  const [statusMessage, setStatusMessage] = useState({ type: "", text: "" });

  // Maintenance Selector Registries
  const [bomList, setBomList] = useState([]);
  const [targetBomId, setTargetBomId] = useState("");

  // Loaded Assembly Scope Context
  const [currentBomId, setCurrentBomId] = useState(null);
  const [bomName, setBomName] = useState("");
  const [bomDesc, setBomDesc] = useState("");
  const [details, setDetails] = useState([
    { parts_id: 1, parts_code: "", parts_desc: "", parts_uom: "", parts_qty: "0", parts_notes: "" }
  ]);

  // --- Fetch Assembly List on Load ---
  const loadBomDropdownRegistry = async () => {
    try {
      const response = await fetch("/api/data?scope=bom&action=list-boms");
      const data = await response.json();
      if (response.ok && data.success) {
        setBomList(data.boms || []);
      }
    } catch (err) {
      console.error("Failed to rehydrate directory registry options:", err);
    }
  };

  useEffect(() => {
    loadBomDropdownRegistry();
  }, []);

  // --- Fetch Existing Complete Record Framework ---
  const handleLoadSelectedBOM = async (bomIdToLoad) => {
    if (!bomIdToLoad) return;
    setLoadingRecord(true);
    setStatusMessage({ type: "", text: "" });

    try {
      const response = await fetch(`/api/data?scope=bom&action=get-bom&bom_id=${bomIdToLoad}`);
      const outcome = await response.json();

      if (response.ok && outcome.success) {
        setCurrentBomId(outcome.header.bom_id);
        setBomName(outcome.header.bom_name || "");
        setBomDesc(outcome.header.bom_desc || "");
        
        if (outcome.details && outcome.details.length > 0) {
          setDetails(outcome.details.map(d => ({
            ...d,
            parts_qty: String(d.parts_qty)
          })));
        } else {
          setDetails([{ parts_id: 1, parts_code: "", parts_desc: "", parts_uom: "", parts_qty: "0", parts_notes: "" }]);
        }
        setStatusMessage({ type: "success", text: `Loaded assembly profile "${outcome.header.bom_name}" successfully.` });
      } else {
        setStatusMessage({ type: "error", text: outcome.message || "Failed to retrieve specified record parameters." });
      }
    } catch (err) {
      setStatusMessage({ type: "error", text: "Network layer exception halted rehydration lookup pipeline." });
    } finally {
      setLoadingRecord(false);
    }
  };

  // --- Reset Form Handler ---
  const handleReset = () => {
    setCurrentBomId(null);
    setTargetBomId("");
    setBomName("");
    setBomDesc("");
    setDetails([{ parts_id: 1, parts_code: "", parts_desc: "", parts_uom: "", parts_qty: "0", parts_notes: "" }]);
    setStatusMessage({ type: "", text: "" });
  };

  // --- Line Grid Modifiers ---
  const addDetailRow = () => {
    setDetails(prev => [
      ...prev,
      { parts_id: prev.length + 1, parts_code: "", parts_desc: "", parts_uom: "", parts_qty: "0", parts_notes: "" }
    ]);
  };

  const removeDetailRow = (indexToRemove) => {
    if (details.length === 1) {
      setStatusMessage({ type: "error", text: "At least one material part specification row is mandatory." });
      return;
    }
    const filtered = details.filter((_, idx) => idx !== indexToRemove);
    setDetails(filtered.map((row, idx) => ({ ...row, parts_id: idx + 1 })));
  };

  const handleDetailChange = (index, field, value) => {
    setDetails(prev => prev.map((row, idx) => {
      if (idx !== index) return row;
      return { ...row, [field]: value };
    }));
  };

  // --- Transactional Commit (Atomic Creation / Modification Update) ---
  const handleSaveBOM = async (e) => {
    if (e) e.preventDefault();
    if (!bomName.trim()) {
      setStatusMessage({ type: "error", text: "BOM Name is a required field." });
      return;
    }

    setSubmitting(true);
    setStatusMessage({ type: "", text: "" });

    try {
      const response = await fetch("/api/data", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          scope: "bom",
          header: {
            bom_id: currentBomId, // Passes existing key if loaded, otherwise creates a new record
            bom_type: 41, 
            bom_name: bomName.trim(),
            bom_desc: bomDesc.trim() || null
          },
          details: details.map((d, idx) => ({
            ...d,
            parts_id: d.parts_id ? Number(d.parts_id) : (idx + 1),
            parts_qty: Number(d.parts_qty) || 0
          }))
        })
      });

      const outcome = await response.json();
      
      if (response.ok && outcome.success) {
        setStatusMessage({ 
          type: "success", 
          text: currentBomId 
            ? "Existing configuration profile changes successfully synced and committed down to database tables." 
            : "Brand new Bill of Materials assembly safely registered into backend systems." 
        });
        
        // Refresh lookup dropdown choices to reflect additions/modifications
        await loadBomDropdownRegistry();
        
        // Reset view panel
        handleReset();
      } else {
        setStatusMessage({ type: "error", text: outcome.message || "Failed to commit transactional database entry." });
      }
    } catch (err) {
      setStatusMessage({ type: "error", text: "A communication layer anomaly disrupted operation workflow." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Page>
      <style>{`
        .app-page { max-width: 1400px !important; }
        .bom-table input.text-input { width: 100%; box-sizing: border-box; }
        .maintenance-select { padding: 8px 12px; font-size: 14px; border-radius: 8px; border: 1px solid #ddd; min-width: 280px; }
      `}</style>

      <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
        
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h2 style={{ margin: 0, fontSize: "22px", fontWeight: 600 }}>
            {currentBomId ? `Maintain Existing BOM (ID: ${currentBomId})` : "Create Bill of Materials (BOM)"}
          </h2>
        </div>

        {/* Dynamic Maintenance Records Loader Selector Row Bar */}
        <div className="control-row" style={{ background: "#f0f4f8", padding: "12px 16px", borderRadius: "12px", border: "1px solid #d0d7de", justifyContent: "space-between", flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <span style={{ fontWeight: 600, fontSize: "14px", color: "#24292f" }}>Maintain Existing Component Matrix:</span>
            <select 
              className="maintenance-select"
              value={targetBomId}
              onChange={(e) => setTargetBomId(e.target.value)}
              disabled={loadingRecord || submitting}
            >
              <option value="">-- Select an assembly from registry --</option>
              {bomList.map(b => (
                <option key={b.bom_id} value={b.bom_id}>{b.bom_name}</option>
              ))}
            </select>
            <Button 
              onClick={() => handleLoadSelectedBOM(targetBomId)} 
              disabled={!targetBomId || loadingRecord || submitting}
              style={{ backgroundColor: "#24292f", color: "#fff", borderColor: "#1b1f23" }}
            >
              {loadingRecord ? "Loading Profile..." : "Load Data"}
            </Button>
          </div>

          {/* Standard Data Buttons Action Matrix Controllers */}
          <div style={{ display: "flex", gap: "8px" }}>
            <Button onClick={() => window.history.back()}>← Trở về / Back</Button>
            <Button onClick={handleReset} disabled={submitting || loadingRecord}>New / Clear</Button>
            <Button onClick={handleReset} disabled={submitting || loadingRecord}>Reset</Button>
            <Button onClick={handleSaveBOM} disabled={submitting || loadingRecord || !bomName.trim()} style={{ backgroundColor: "#2563eb", color: "#fff", borderColor: "#1d4ed8", fontWeight: "600" }}>
              {submitting ? "Processing..." : "Save Changes"}
            </Button>
          </div>
        </div>

        {statusMessage.text && (
          <div style={{
            padding: "12px 16px",
            borderRadius: "8px",
            backgroundColor: statusMessage.type === "error" ? "#fef2f2" : "#f0fdf4",
            border: `1px solid ${statusMessage.type === "error" ? "#fca5a5" : "#86efac"}`,
            color: statusMessage.type === "error" ? "#991b1b" : "#166534",
            fontWeight: 500
          }}>
            {statusMessage.text}
          </div>
        )}

        {/* 1. MAIN HEADER CONFIGURATION BLOCK */}
        <div className="page-section" style={{ opacity: loadingRecord ? 0.6 : 1 }}>
          <h3 style={{ margin: "0 0 16px 0", fontSize: "15px", borderBottom: "1px solid #eee", paddingBottom: "8px", fontWeight: 600 }}>
            1. Main Header Configuration
          </h3>
          
          <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
            <div className="field">
              <label style={{ display: "block" }}>
                <span className="label">BOM Name</span>
                <input
                  type="text"
                  className="text-input"
                  value={bomName}
                  onChange={(e) => setBomName(e.target.value)}
                  placeholder="Enter custom assembly matrix identifier"
                  disabled={loadingRecord || submitting}
                />
              </label>
            </div>

            <div className="field">
              <AutoExpandingTextArea
                label="BOM Description"
                value={bomDesc}
                onChange={(e) => setBomDesc(e.target.value)}
                placeholder="Enter complete engineering specification data or scope logic notes..."
                disabled={loadingRecord || submitting}
              />
            </div>
          </div>
        </div>

        {/* 2. MATERIAL & PART ALLOCATION DETAILS MATRIX */}
        <div className="page-section" style={{ opacity: loadingRecord ? 0.6 : 1 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px", borderBottom: "1px solid #eee", paddingBottom: "8px" }}>
            <h3 style={{ margin: 0, fontSize: "15px", fontWeight: 600 }}>2. Material & Part Allocation Details</h3>
            <Button 
              onClick={addDetailRow} 
              disabled={loadingRecord || submitting}
              style={{ backgroundColor: "#f0fdf4", borderColor: "#bbf7d0", color: "#166534", padding: "6px 12px" }}
            >
              + Add Part Row
            </Button>
          </div>

          <div style={{ overflowX: "auto" }}>
            <table className="bom-table" style={{ width: "100%", borderCollapse: "collapse", layout: "fixed" }}>
              <thead>
                <tr style={{ backgroundColor: "#f8f9fa", borderBottom: "2px solid #e5e5e5" }}>
                  <th style={{ padding: "10px", textAlign: "left", fontSize: "13px", fontWeight: 600, width: "90px" }}>Part ID</th>
                  <th style={{ padding: "10px", textAlign: "left", fontSize: "13px", fontWeight: 600, width: "140px" }}>Part Code</th>
                  <th style={{ padding: "10px", textAlign: "left", fontSize: "13px", fontWeight: 600, width: "300px" }}>Description</th>
                  <th style={{ padding: "10px", textAlign: "left", fontSize: "13px", fontWeight: 600, width: "95px" }}>UOM</th>
                  <th style={{ padding: "10px", textAlign: "left", fontSize: "13px", fontWeight: 600, width: "100px" }}>Quantity</th>
                  <th style={{ padding: "10px", textAlign: "left", fontSize: "13px", fontWeight: 600, width: "320px" }}>Line Notes</th>
                  <th style={{ padding: "10px", textAlign: "center", fontSize: "13px", fontWeight: 600, width: "60px" }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {details.map((row, index) => (
                  <tr key={index} style={{ borderBottom: "1px solid #eee" }}>
                    <td style={{ padding: "8px 6px", verticalAlign: "top" }}>
                      <input
                        type="number"
                        className="text-input"
                        value={row.parts_id}
                        onChange={(e) => handleDetailChange(index, "parts_id", e.target.value)}
                        placeholder={String(index + 1)}
                        min="1"
                        style={{ fontWeight: "600", color: "#2563eb", backgroundColor: "#fbffe7" }}
                        disabled={loadingRecord || submitting}
                      />
                    </td>
                    <td style={{ padding: "8px 6px", verticalAlign: "top" }}>
                      <input
                        type="text"
                        className="text-input"
                        value={row.parts_code || ""}
                        onChange={(e) => handleDetailChange(index, "parts_code", e.target.value)}
                        placeholder="P-100x"
                        disabled={loadingRecord || submitting}
                      />
                    </td>
                    <td style={{ padding: "8px 6px", verticalAlign: "top" }}>
                      <AutoExpandingTextArea
                        value={row.parts_desc || ""}
                        onChange={(e) => handleDetailChange(index, "parts_desc", e.target.value)}
                        placeholder="Component specifications"
                        disabled={loadingRecord || submitting}
                      />
                    </td>
                    <td style={{ padding: "8px 6px", verticalAlign: "top" }}>
                      <input
                        type="text"
                        className="text-input"
                        value={row.parts_uom || ""}
                        onChange={(e) => handleDetailChange(index, "parts_uom", e.target.value)}
                        placeholder="pcs/m"
                        disabled={loadingRecord || submitting}
                      />
                    </td>
                    <td style={{ padding: "8px 6px", verticalAlign: "top" }}>
                      <input
                        type="number"
                        className="text-input"
                        value={row.parts_qty || "0"}
                        onChange={(e) => handleDetailChange(index, "parts_qty", e.target.value)}
                        min="0"
                        step="any"
                        disabled={loadingRecord || submitting}
                      />
                    </td>
                    <td style={{ padding: "8px 6px", verticalAlign: "top" }}>
                      <AutoExpandingTextArea
                        value={row.parts_notes || ""}
                        onChange={(e) => handleDetailChange(index, "parts_notes", e.target.value)}
                        placeholder="Optional contextual remarks..."
                        disabled={loadingRecord || submitting}
                      />
                    </td>
                    <td style={{ padding: "8px 6px", textAlign: "center", verticalAlign: "top" }}>
                      <button
                        type="button"
                        onClick={() => removeDetailRow(index)}
                        disabled={loadingRecord || submitting}
                        style={{
                          background: "none",
                          border: "none",
                          color: (loadingRecord || submitting) ? "#ccc" : "#dc2626",
                          cursor: (loadingRecord || submitting) ? "default" : "pointer",
                          fontSize: "20px",
                          fontWeight: "bold",
                          lineHeight: "1.2",
                          marginTop: "6px"
                        }}
                        title="Delete record row line"
                      >
                        &times;
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

      </div>
    </Page>
  );
}